import { lazy, type ComponentType, type LazyExoticComponent } from "react";

type PropsOf<C> = C extends ComponentType<infer P> ? P : never;

/**
 * A screen for a registry page, loaded when the page first opens:
 * `const Sourcing = screen(() => import("../screens/find/Find"), "Sourcing");` then `render: () => <Sourcing />`.
 *
 * Registries load screens this way, never with a plain `import { Sourcing } from "../screens/..."`: screens read
 * lib/nav (kindsFor), and lib/nav reads the registries, so a plain import makes a loop in which a registry is still
 * empty when the menu is built. modules/registry.test.ts fails if a registry imports anything but ./types and
 * ./screen. The page shows "Opening the page" for the moment a screen takes to load.
 */
export function screen<M, K extends keyof M>(load: () => Promise<M>, name: K): LazyExoticComponent<ComponentType<PropsOf<M[K]>>> {
  return lazy(async () => {
    const m = await load();
    const C = m[name];
    if (!C) throw new Error(`No screen called ${String(name)}`);
    return { default: C as unknown as ComponentType<PropsOf<M[K]>> };
  });
}
